import type { DtcCodeEntity } from '@packages/domain/dtc-codes';

import { noop } from '@packages/core/utils';
import { TableRenderer } from '@web/components/table';
import { useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';

import { DtcCodesTableHead } from './dtc-codes-table-head';
import { DtcCodesTableRow } from './dtc-codes-table-row';

/**
 * Props for the {@link DtcCodesTable} component.
 */
type DtcCodesTableProps = {
    /** The list of DTC codes to display in the table. */
    dtcCodeData: DtcCodeEntity[];

    /** Per-code loading state keyed by DTC code string. */
    isDtcLoading: { [dtcCode: string]: boolean };

    /** Whether a primary code change is currently in flight. */
    isPrimaryUpdating: boolean;

    /** Callback to delete a DTC code. */
    onDelete: (code: DtcCodeEntity) => void;

    /** Callback to toggle freeze frame data for a DTC code. */
    onFreezeFrameChange: (code: DtcCodeEntity) => void;

    /** Callback to change the primary DTC code. */
    onPrimaryChange: (code: DtcCodeEntity) => void;
};

/**
 * Renders a table of DTC codes with controls for deleting, setting primary, and toggling freeze frame status.
 * Each row delegates rendering to {@link DtcCodesTableRow}.
 *
 * @param props - the component props
 * @returns the DTC codes table
 */
export function DtcCodesTable({
    dtcCodeData,
    isDtcLoading,
    isPrimaryUpdating,
    onDelete,
    onFreezeFrameChange,
    onPrimaryChange,
}: DtcCodesTableProps) {
    const classes = useStyles();

    const tableData = useMemo(() => dtcCodeData.map((dtc) => ({ ...dtc, rowId: dtc.code })), [dtcCodeData]);

    // Handlers

    /** Renders a single DTC code row in the table body. */
    const handleRenderBodyRow = useCallback(
        (tableRowData: DtcCodeEntity) => (
            <DtcCodesTableRow
                isLoading={Boolean(isDtcLoading[tableRowData.code])}
                isPrimaryUpdating={isPrimaryUpdating}
                key={tableRowData.code}
                onDelete={onDelete}
                onFreezeFrameChange={onFreezeFrameChange}
                onPrimaryChange={onPrimaryChange}
                rowData={tableRowData}
            />
        ),
        [isDtcLoading, isPrimaryUpdating, onDelete, onFreezeFrameChange, onPrimaryChange]
    );

    /** Renders the table header row. */
    const handleRenderHeaderRow = useCallback(() => <DtcCodesTableHead />, []);

    return (
        <TableRenderer
            emptyStateProps={{
                description: 'No added DTC codes',
            }}
            outerTableClassName={classes.table}
            tableBodyRowRenderer={handleRenderBodyRow}
            tableHeaderRowRenderer={handleRenderHeaderRow}
            tableRowsData={tableData}
            tableRowsDataSetter={noop}
        />
    );
}

const useStyles = createUseStyles({
    table: {
        height: 'auto',
        minHeight: 'unset',
        maxHeight: 'max-content',
    },
});
